import React, { createContext, useContext, useState, useEffect } from 'react';

// Seasonal themes designed for military audience - modern, professional, yet warm
// Universal for all of Europe
export const SEASONS = {
  winter: {
    name: 'Winter',
    months: [12, 1, 2],
    colors: {
      primary: '#3b82f6', // Signal blue
      secondary: '#94a3b8', // Frost silver
      accent: '#0ea5e9', // Ice blue
      heroGradient: 'from-slate-950 via-blue-950/30 to-slate-950',
      cardGlow: 'rgba(59, 130, 246, 0.15)',
      seasonalAccent: '#e0f2fe', // Light ice
    },
    icons: ['❄️', '🎄', '☃️'],
    heroImage: 'https://images.unsplash.com/photo-1482192505345-5655af888cc4?w=1920&h=1080&fit=crop', // Snowy European town
    tagline: 'Find Your Warm Home This Winter',
    features: ['Christmas Markets', 'Cozy Fireplaces', 'Winter Sports'],
  },
  spring: {
    name: 'Spring',
    months: [3, 4, 5],
    colors: {
      primary: '#22c55e', // Fresh green
      secondary: '#a3e635', // Lime
      accent: '#fbbf24', // Warm yellow
      heroGradient: 'from-slate-950 via-emerald-950/20 to-slate-950',
      cardGlow: 'rgba(34, 197, 94, 0.15)',
      seasonalAccent: '#dcfce7', // Light green
    },
    icons: ['🌸', '🌷', '🐣'],
    heroImage: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1920&h=1080&fit=crop', // Spring flowers
    tagline: 'Fresh Starts in Your New European Home',
    features: ['Blooming Gardens', 'Easter Festivals', 'Outdoor Terraces'],
  },
  summer: {
    name: 'Summer',
    months: [6, 7, 8],
    colors: {
      primary: '#f59e0b', // Warm amber
      secondary: '#fbbf24', // Golden
      accent: '#3b82f6', // Cool blue contrast
      heroGradient: 'from-slate-950 via-amber-950/20 to-slate-950',
      cardGlow: 'rgba(245, 158, 11, 0.15)',
      seasonalAccent: '#fef3c7', // Light amber
    },
    icons: ['☀️', '🍺', '🏖️'],
    heroImage: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1920&h=1080&fit=crop', // European summer cityscape
    tagline: 'Your Summer Adventure Starts Here',
    features: ['Beer Gardens', 'Outdoor Festivals', 'Beach Access'],
  },
  autumn: {
    name: 'Autumn',
    months: [9, 10, 11],
    colors: {
      primary: '#ea580c', // Burnt orange
      secondary: '#dc2626', // Deep red
      accent: '#d97706', // Military gold (matches brand)
      heroGradient: 'from-slate-950 via-orange-950/20 to-slate-950',
      cardGlow: 'rgba(234, 88, 12, 0.15)',
      seasonalAccent: '#ffedd5', // Light orange
    },
    icons: ['🍂', '🎃', '🍷'],
    heroImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&h=1080&fit=crop', // Autumn forest
    tagline: 'Settle In Before the Holidays',
    features: ['Wine Festivals', 'Oktoberfest', 'Forest Hikes'],
  },
};

// Get current season based on month
export const getCurrentSeason = () => {
  const month = new Date().getMonth() + 1; // 1-12
  for (const [season, data] of Object.entries(SEASONS)) {
    if (data.months.includes(month)) {
      return season;
    }
  }
  return 'winter'; // default
};

const SeasonContext = createContext(null);

export function SeasonProvider({ children }) {
  const [currentSeason, setCurrentSeason] = useState(getCurrentSeason());
  const [manualOverride, setManualOverride] = useState(() => {
    const saved = localStorage.getItem('tla-season-override');
    return saved || null;
  });

  const season = manualOverride || currentSeason;
  const theme = SEASONS[season];

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--season-primary', theme.colors.primary);
    root.style.setProperty('--season-secondary', theme.colors.secondary);
    root.style.setProperty('--season-accent', theme.colors.accent);
    root.style.setProperty('--season-glow', theme.colors.cardGlow);
  }, [theme]);

  useEffect(() => {
    if (manualOverride) {
      localStorage.setItem('tla-season-override', manualOverride);
    } else {
      localStorage.removeItem('tla-season-override');
    }
  }, [manualOverride]);

  const setSeason = (newSeason) => {
    setManualOverride(newSeason);
  };

  const cycleSeason = () => {
    const seasons = Object.keys(SEASONS);
    const currentIndex = seasons.indexOf(season);
    const nextIndex = (currentIndex + 1) % seasons.length;
    setManualOverride(seasons[nextIndex]);
  };

  const resetToAuto = () => {
    setManualOverride(null);
  };

  return (
    <SeasonContext.Provider value={{ 
      season, 
      theme, 
      setSeason,
      cycleSeason, 
      resetToAuto,
      isManualOverride: !!manualOverride,
      allSeasons: SEASONS 
    }}>
      {children}
    </SeasonContext.Provider>
  );
}

export function useSeason() {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
}
